import { REQUEST_CONFIGS_KEYS } from '@services/constants'

import { STEP1FIELDS, useStep1State } from './initialStates/step1State'
import { STEP2FIELDS, useStep2State } from './initialStates/step2State'
import { STEP3FIELDS, useStep3State } from './initialStates/step3State'
import { STEP4_1FIELDS, useStep4_1State } from './initialStates/step4_1State'
import { STEP5FIELDS, useStep5State } from './initialStates/step5State'
import { STEP4_4FIELDS, useStep4_4State } from './initialStates/step4_4State'

import {
	isBalconySectionA,
	isBalconySectionB,
	isGR6_2SectionA,
	isGR6_2SectionB,
	isGR6_2SectionC,
	isGR6_2SectionD,
	isGR6_2SectionE,
	isGR6_2SectionF,
	isBalconyOrPorchPermitGR6_1,
	isResidentialLandPermitGR6_2,
	isResidentialLandPermitGR6_5,
	isTypeOfWorkSectionA,
	isTypeOfWorkSectionB,
	isGR6_3ParkingSectionW,
	isGR6_3ParkingSectionX,
	isGR6_3ParkingSectionY,
	isGR6_3TypesOfWorkToPerformParkingSectionB,
	isGR6_3TypesOfWorkToPerformParkingSectionC,
	isResidentialLandPermitGR6_3,
	isResidentialLandPermitGR6_4,
	isGR6_4SectionA,
	isGR6_4SectionB,
	isGR6_4SectionC
} from './__index.utils'
import * as pageUtils from '../__forms.utils'
import { STEP4_2FIELDS, useStep4_2State } from './initialStates/step4_2State'
import { STEP4_5FIELDS, useStep4_5State } from './initialStates/step4_5State'
import { STEP4_3FIELDS, useStep4_3State } from './initialStates/step4_3State'
import { STEP6FIELDS, useStep6State } from './initialStates/step6State'

// --- 1. Initial state HOOK ---
export function useInitialFormState() {
	return {
		...useStep1State(),
		...useStep2State(),
		...useStep3State(),
		...useStep4_1State(),
		...useStep4_2State(),
		...useStep4_5State(),
		...useStep4_4State(),
		...useStep5State(),
		...useStep4_3State(),
		...useStep6State()
	}
}

// --- 2. Logique modulaire des dépendances dynamiques ---
function updateLinkedFields(state, changedField: string, value: string) {
	type StateType = ReturnType<typeof useInitialFormState>
	const updates: Partial<StateType> = {}

	switch (changedField) {
		// ! STEP 1
		/**  @whatToDo - Que souhaitez-vous faire? */
		case STEP1FIELDS.whatToDo: {
			const isGR6_1 = isBalconyOrPorchPermitGR6_1(value)
			const isGR6_2 = isResidentialLandPermitGR6_2(value)
			const isGR6_3 = isResidentialLandPermitGR6_3(value)
			const isGR6_4 = isResidentialLandPermitGR6_4(value)
			const isGR6_5 = isResidentialLandPermitGR6_5(value)

			// ************************************************
			// PIIA reset fields
			updates.isSubjectToPiiaApproval = {
				...state.isSubjectToPiiaApproval,
				required: !isGR6_4,
				value: ''
			}

			const piiaFieldsToReset = [
				STEP1FIELDS.hasBeenSubjectToApproval,
				STEP1FIELDS.iaNumber,
				STEP1FIELDS.ecResolutionNumber
			]

			piiaFieldsToReset.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: false,
						value: ''
					}
				}
			})
			// ** Update Step 3
			// Group GR6.1
			updates.mainUseOfExistingBuilding = {
				...state.mainUseOfExistingBuilding,
				required: isGR6_1 || isGR6_3,
				value: !(isGR6_1 || isGR6_3)
					? ''
					: state.mainUseOfExistingBuilding.value
			}
			updates.structureType = {
				...state.structureType,
				required: isGR6_1 || isGR6_5,
				value: !isGR6_1 && !isGR6_5 ? '' : state.structureType.value
			}

			// Group GR6.2
			updates.useOfBuildingOrLand = {
				...state.useOfBuildingOrLand,
				required: isGR6_2 || isGR6_4 || isGR6_5,
				value:
					!isGR6_2 && !isGR6_4 && !isGR6_5
						? ''
						: state.useOfBuildingOrLand.value
			}

			updates.isResidentialOrOther = {
				...state.isResidentialOrOther,
				required:
					(isGR6_1 &&
						state.mainUseOfExistingBuilding.value ==
							REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.fourthOption) ||
					(isGR6_2 &&
						state.useOfBuildingOrLand.value ==
							REQUEST_CONFIGS_KEYS.useOfBuildingOrLand
								.agriculturalIndustrialInstitutionalCommercialOrMixed) ||
					(isGR6_3 &&
						[
							REQUEST_CONFIGS_KEYS.mainUseOfExistingBuildingForParking
								.fourthOption,
							REQUEST_CONFIGS_KEYS.mainUseOfExistingBuildingForParking
								.fifthOption
						].includes(state.mainUseOfExistingBuilding.value)),
				value: !isGR6_1 ? '' : state.isResidentialOrOther.value
			}

			updates.isSubjectToPiiaApproval = {
				...state.isSubjectToPiiaApproval,
				required: !isGR6_4,
				value: !isGR6_4 ? state.isSubjectToPiiaApproval.value : ''
			}
			// ************************************************

			// ************************************************
			// !important RESET FIELDS of Steps 4.1 and 4.2
			const step4_1FieldsToReset = Object.values(STEP4_1FIELDS)
			const step4_2FieldsToReset = Object.values(STEP4_2FIELDS)
			const step4_3FieldsToReset = Object.values(STEP4_3FIELDS)
			const step4_4FieldsToReset = Object.values(STEP4_4FIELDS)
			const step4_5FieldsToReset = Object.values(STEP4_5FIELDS)

			const fieldsToReset = [
				...step4_1FieldsToReset,
				...step4_2FieldsToReset,
				...step4_3FieldsToReset,
				...step4_4FieldsToReset,
				...step4_5FieldsToReset
			]

			fieldsToReset.forEach((value) => {
				if (state[value]) {
					switch (value) {
						case STEP4_1FIELDS.typesOfWorkBalcony:
							updates.typesOfWorkBalcony = {
								...state.typesOfWorkBalcony,
								required: isGR6_1,
								value: ''
							}
							break
						case STEP4_2FIELDS.typesOfWorkResidentialLand:
							updates.typesOfWorkResidentialLand = {
								...state.typesOfWorkResidentialLand,
								required: isGR6_2,
								value: ''
							}
							break

						case STEP4_3FIELDS.blockNumber:
							updates.blockNumber = {
								...state.blockNumber,
								value: 1
							}
							break

						case STEP4_3FIELDS.typesOfWorkToPerformParking:
							updates.typesOfWorkToPerformParking = {
								...state.typesOfWorkToPerformParking,
								required: isGR6_3,
								value: ''
							}
							break

						case STEP4_4FIELDS.typesOfWorkBoat:
							updates.typesOfWorkBoat = {
								...state.typesOfWorkBoat,
								required: isGR6_4,
								value: ''
							}
							break

						case STEP4_5FIELDS.typesOfWorkDiverse:
							updates.typesOfWorkDiverse = {
								...state.typesOfWorkDiverse,
								required: isGR6_5,
								value: ''
							}
							break

						default:
							updates[value] = {
								...state[value],
								required: false,
								value: ''
							}
					}
				}
			})

			// ************************************************
			// ** Reset Step 6 fields
			// ************************************************
			updates.isWaterAndSewerConnectionRequired = {
				...state.isWaterAndSewerConnectionRequired,
				required: isGR6_3,
				value: isGR6_3 ? state.isWaterAndSewerConnectionRequired.value : ''
			}

			break
		}

		/**  @requestAs - Q2 Vous faites cette demande en tant que: */
		case STEP1FIELDS.requestAs: {
			const isEntreprise = value == REQUEST_CONFIGS_KEYS.requestAs.entreprise

			updates.companyName = {
				...state.companyName,
				required: isEntreprise,
				value: isEntreprise ? state.companyName.value : ''
			}
			updates.representativeName = {
				...state.representativeName,
				required: isEntreprise,
				value: isEntreprise ? state.representativeName.value : ''
			}

			// reset whoWillBeInChargeOfWork
			if (
				value == REQUEST_CONFIGS_KEYS.requestAs.individual &&
				state.whoWillBeInChargeOfWork.value ==
					REQUEST_CONFIGS_KEYS.whoWillBeInChargeOfWork.firstOption
			) {
				updates.whoWillBeInChargeOfWork = {
					...state.whoWillBeInChargeOfWork,
					value: ''
				}
			}
			break
		}

		/** @isSubjectToPiiaApproval - Est-ce que les travaux sont assujettis à une approbation PIIA? */
		case STEP1FIELDS.isSubjectToPiiaApproval: {
			const isYesPiiaApproval = value == 'true'
			updates.hasBeenSubjectToApproval = {
				...state.hasBeenSubjectToApproval,
				required: isYesPiiaApproval,
				value: isYesPiiaApproval ? state.hasBeenSubjectToApproval.value : ''
			}
			updates.iaNumber = {
				...state.iaNumber,
				required:
					isYesPiiaApproval && state.hasBeenSubjectToApproval.value === 'true',
				value: isYesPiiaApproval ? state.iaNumber.value : ''
			}
			break
		}

		/**  @hasBeenSubjectToApproval - Le projet a-t-il reçu une approbation PIIA? */
		case STEP1FIELDS.hasBeenSubjectToApproval: {
			const isYesHasBeenSubject = value == 'true'
			updates.iaNumber = {
				...state.iaNumber,
				required:
					isYesHasBeenSubject && state.isSubjectToPiiaApproval.value === 'true',
				value: isYesHasBeenSubject ? state.iaNumber.value : ''
			}
			break
		}

		// ***********************************************************
		// ! STEP 2
		/**  @isLocationOwned */
		case STEP2FIELDS.isLocationOwned: {
			const isYes = value == 'false'

			// updates.ownerFirstName = {
			// 	...state.ownerFirstName,
			// 	required: isYes
			// }
			// updates.ownerLastName = {
			// 	...state.ownerLastName,
			// 	required: isYes
			// }
			// updates.ownerEmail = {
			// 	...state.ownerEmail,
			// 	required: isYes
			// }
			// updates.ownerPhoneNumber = {
			// 	...state.ownerPhoneNumber,
			// 	required: isYes
			// }

			const fieldsToMakeUpdate = [
				STEP2FIELDS.ownerFirstName,
				STEP2FIELDS.ownerLastName,
				STEP2FIELDS.ownerEmail,
				STEP2FIELDS.ownerPhoneNumber
			]

			fieldsToMakeUpdate.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isYes,
						value: isYes ? state[key].value : ''
					}
				}
			})

			break
		}

		// ***********************************************************
		// ! STEP 3
		/**  @useOfBuildingOrLand *Pour le groupe GR6.2 "Quel est l'usage du bâtiment ou du terrain?" */
		case STEP3FIELDS.useOfBuildingOrLand: {
			const isRequired =
				[
					REQUEST_CONFIGS_KEYS.useOfBuildingOrLand
						.agriculturalIndustrialInstitutionalCommercialOrMixed,
					REQUEST_CONFIGS_KEYS.useOfBuildingOrLand.agriculturalBuildingOrLand
				].includes(value) && !isResidentialLandPermitGR6_4(state.whatToDo.value)

			updates.isResidentialOrOther = {
				...state.isResidentialOrOther,
				required: isRequired,
				value: isRequired ? state.isResidentialOrOther.value : ''
			}

			updates.existingMainBuildingIsShared = {
				...state.existingMainBuildingIsShared,
				required: value !== REQUEST_CONFIGS_KEYS.useOfBuildingBoat.emptyLot,
				value:
					value !== REQUEST_CONFIGS_KEYS.useOfBuildingBoat.emptyLot
						? state.existingMainBuildingIsShared.value
						: ''
			}

			break
		}

		/**  @mainUseOfExistingBuilding  "Quel est l'usage du bâtiment principal existant?" */
		case STEP3FIELDS.mainUseOfExistingBuilding: {
			const isGR6_3 = isResidentialLandPermitGR6_3(state.whatToDo.value)

			const isRequired =
				value == REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.fourthOption ||
				(isGR6_3 &&
					value ==
						REQUEST_CONFIGS_KEYS.mainUseOfExistingBuildingForParking
							.fifthOption)
			updates.isResidentialOrOther = {
				...state.isResidentialOrOther,
				required: isRequired,
				value: isRequired ? state.isResidentialOrOther.value : ''
			}
			//* no Break here to continue with the next case
		}

		// ***********************************************************
		// ! STEP 4.1
		/**  @mainUseOfExistingBuilding  "Quel est l'usage du bâtiment principal existant?" */
		/**  @typesOfWorkBalcony  Quel(s) type(s) de travaux souhaitez-vous effectuer? */
		case STEP3FIELDS.mainUseOfExistingBuilding:
		case STEP4_1FIELDS.typesOfWorkBalcony: {
			const isSectionA = isBalconySectionA(state.typesOfWorkBalcony.value)
			const isSectionB = isBalconySectionB(state.typesOfWorkBalcony.value)

			const isConditionsValidAboutMainUse = [
				REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.firstOption,
				REQUEST_CONFIGS_KEYS.mainUseOfExistingBuilding.thirdOption
			].includes(state.mainUseOfExistingBuilding.value)

			//* BALCONY SECTION A
			// Q12
			updates.installationType = {
				...state.installationType,
				required: isSectionA && isConditionsValidAboutMainUse,
				value:
					isSectionA && isConditionsValidAboutMainUse
						? state.installationType.value
						: ''
			}
			// Q13
			updates.foundationTypeBalcony = {
				...state.foundationTypeBalcony,
				required: isSectionA && isConditionsValidAboutMainUse,
				value:
					isSectionA && isConditionsValidAboutMainUse
						? state.foundationTypeBalcony.value
						: ''
			}
			// Q14
			updates.locationBalcony = {
				...state.locationBalcony,
				required: isSectionA
			}

			//* TERRACE SECTION B
			// Q21
			updates.foundationTypeTerrace = {
				...state.foundationTypeTerrace,
				required: isSectionB
			}
			// Q22
			updates.locationTerrace = {
				...state.locationTerrace,
				required: isSectionB
			}
			break
		}

		// *********************************************************
		// ! STEP 4.2
		/**  @typesOfWorkResidentialLand  Quel(s) type(s) de travaux souhaitez-vous effectuer? */
		case STEP4_2FIELDS.typesOfWorkResidentialLand: {
			const isSectionA = isGR6_2SectionA(state.typesOfWorkResidentialLand.value)
			const isSectionB = isGR6_2SectionB(state.typesOfWorkResidentialLand.value)
			const isSectionC = isGR6_2SectionC(state.typesOfWorkResidentialLand.value)
			const isSectionD = isGR6_2SectionD(state.typesOfWorkResidentialLand.value)
			const isSectionE = isGR6_2SectionE(state.typesOfWorkResidentialLand.value)
			const isSectionF = isGR6_2SectionF(state.typesOfWorkResidentialLand.value)

			//* SECTION A
			updates.areaOfWorkPreparationTerrain = {
				...state.areaOfWorkPreparationTerrain,
				required: isSectionA,
				value: isSectionA ? state.areaOfWorkPreparationTerrain.value : ''
			}
			//* SECTION B
			updates.areaOfWorkRemblaiDeblaiDecontamination = {
				...state.areaOfWorkRemblaiDeblaiDecontamination,
				required: isSectionB,
				value: isSectionB
					? state.areaOfWorkRemblaiDeblaiDecontamination.value
					: ''
			}
			//* SECTION C
			const fieldsSectionC = [
				STEP4_2FIELDS.isWallAdditionOrRelocationMuret,
				STEP4_2FIELDS.heightWallMuret,
				STEP4_2FIELDS.lengthWallMuret,
				// STEP4_2FIELDS.materialExistingWallMuret, (juste remise à null ci-dessous)
				STEP4_2FIELDS.materialProjectedWallMuret
			]
			fieldsSectionC.forEach((value) => {
				if (state[value]) {
					updates[value] = {
						...state[value],
						required: isSectionC,
						value: isSectionC ? state[value].value : ''
					}
				}
			})
			updates.materialExistingWallMuret = {
				...state.materialExistingWallMuret,
				value: isSectionC ? state.materialExistingWallMuret.value : ''
			}

			//* SECTION D
			const fieldsSectionD = [
				STEP4_2FIELDS.isWallAdditionOrRelocationMurSoutenement,
				STEP4_2FIELDS.heightWallMurSoutenement,
				STEP4_2FIELDS.lengthWallMurSoutenement,
				// STEP4_2FIELDS.materialExistingWallMurSoutenement, (juste remise à null ci-dessous)
				STEP4_2FIELDS.materialProjectedWallMurSoutenement
			]
			fieldsSectionD.forEach((value) => {
				if (state[value]) {
					updates[value] = {
						...state[value],
						required: isSectionD,
						value: isSectionD ? state[value].value : ''
					}
				}
			})
			updates.materialExistingWallMurSoutenement = {
				...state.materialExistingWallMurSoutenement,
				value: isSectionD ? state.materialExistingWallMurSoutenement.value : ''
			}

			//* SECTION E
			updates.areaOfWorkBandeTampon = {
				...state.areaOfWorkBandeTampon,
				required: isSectionE,
				value: isSectionE ? state.areaOfWorkBandeTampon.value : ''
			}
			//* SECTION F
			updates.areaOfWorkAmenagementPaysager = {
				...state.areaOfWorkAmenagementPaysager,
				required: isSectionF,
				value: isSectionF ? state.areaOfWorkAmenagementPaysager.value : ''
			}
			break
		}

		//! STEP 4.3

		case STEP4_3FIELDS.typesOfWorkToPerformParking: {
			const isSectionBC =
				isGR6_3TypesOfWorkToPerformParkingSectionB(
					state.typesOfWorkToPerformParking.value
				) ||
				isGR6_3TypesOfWorkToPerformParkingSectionC(
					state.typesOfWorkToPerformParking.value
				)

			const blockNumber = state.blockNumber.value

			const isSectionBCFields = [
				{
					name: STEP4_3FIELDS.modificationsDrivewayEntranceParking,
					required: isSectionBC
				},
				{
					name: STEP4_3FIELDS.existingWidthParking1,
					required: isSectionBC
				},
				{
					name: STEP4_3FIELDS.desiredWidthParking1,
					required: isSectionBC
				},
				{
					name: STEP4_3FIELDS.existingWidthParking2,
					required: blockNumber >= 2 && isSectionBC
				},
				{
					name: STEP4_3FIELDS.desiredWidthParking2,
					required: blockNumber >= 2 && isSectionBC
				},
				{
					name: STEP4_3FIELDS.existingWidthParking3,
					required: blockNumber === 3 && isSectionBC
				},
				{
					name: STEP4_3FIELDS.desiredWidthParking3,
					required: blockNumber === 3 && isSectionBC
				}
			]

			//fields section w

			isSectionBCFields.forEach((value) => {
				if (state[value.name]) {
					updates[value.name] = {
						...state[value.name],
						required: value.required,
						value: value.required ? state[value.name].value : ''
					}
				}
			})

			break
		}

		case STEP4_3FIELDS.areOtherModificationsInvolvedParking: {
			const isSectionW = isGR6_3ParkingSectionW(
				state.areOtherModificationsInvolvedParking.value
			)
			const isSectionX = isGR6_3ParkingSectionX(
				state.areOtherModificationsInvolvedParking.value
			)
			const isSectionY = isGR6_3ParkingSectionY(
				state.areOtherModificationsInvolvedParking.value
			)

			//fields section w

			const isSectionWFields = [
				STEP4_3FIELDS.wallModificationOrAdditionIsAddingOrMovingWallParking,
				STEP4_3FIELDS.wallModificationOrAdditionWallHeightParking,
				STEP4_3FIELDS.wallModificationOrAdditionWallLengthParking,
				STEP4_3FIELDS.wallModificationOrAdditionProjectedMaterialParking
			]

			const isSectionXFields = [
				STEP4_3FIELDS.retainingWallModificationOrAdditionIsAddingOrMovingWallParking,
				STEP4_3FIELDS.retainingWallModificationOrAdditionWallHeightParking,
				STEP4_3FIELDS.retainingWallModificationOrAdditionWallLengthParking,
				STEP4_3FIELDS.retainingWallModificationOrAdditionProjectedMaterialParking
			]

			const isectionYFields = [
				STEP4_3FIELDS.walkwayModificationOrAdditionWidthParking,
				STEP4_3FIELDS.walkwayModificationOrAdditionProjectedMaterialParking
			]

			isSectionWFields.forEach((value) => {
				if (state[value]) {
					updates[value] = {
						...state[value],
						required: isSectionW,
						value: isSectionW ? state[value].value : ''
					}
				}
			})

			isectionYFields.forEach((value) => {
				if (state[value]) {
					updates[value] = {
						...state[value],
						required: isSectionY,
						value: isSectionY ? state[value].value : ''
					}
				}
			})

			isSectionXFields.forEach((value) => {
				if (state[value]) {
					updates[value] = {
						...state[value],
						required: isSectionX,
						value: isSectionX ? state[value].value : ''
					}
				}
			})

			break
		}

		case STEP4_3FIELDS.blockNumber: {
			const activeBlockCount = state.blockNumber.value
			const typeOfWork = state.typesOfWorkToPerformParking.value

			const isSectionBC =
				isGR6_3TypesOfWorkToPerformParkingSectionB(typeOfWork) ||
				isGR6_3TypesOfWorkToPerformParkingSectionC(typeOfWork)

			const fieldsToUpdate = [
				{
					name: STEP4_3FIELDS.existingWidthParking2,
					required: activeBlockCount >= 2 && isSectionBC
				},
				{
					name: STEP4_3FIELDS.desiredWidthParking2,
					required: activeBlockCount >= 2 && isSectionBC
				},
				{
					name: STEP4_3FIELDS.existingWidthParking3,
					required: activeBlockCount === 3 && isSectionBC
				},
				{
					name: STEP4_3FIELDS.desiredWidthParking3,
					required: activeBlockCount === 3 && isSectionBC
				}
			]

			for (const { name, required } of fieldsToUpdate) {
				updates[name] = {
					...state[name],
					required
				}
			}

			return {
				...state,
				...updates,
				blockNumber: {
					...state.blockNumber,
					value: activeBlockCount
				}
			}
		}

		// ! STEP 4.4
		case STEP4_4FIELDS.typesOfWorkBoat: {
			const isRequiredForPier = state.typesOfWorkBoat.value.includes(
				REQUEST_CONFIGS_KEYS.isBoatDevelopment.pierOrShelter
			)

			const isRequiredForLanding = state.typesOfWorkBoat.value.includes(
				REQUEST_CONFIGS_KEYS.isBoatDevelopment.landingStage
			)

			updates.typesOfStructureBoat = {
				...state.typesOfStructureBoat,
				required: isRequiredForPier,
				value: isRequiredForPier ? state.typesOfStructureBoat.value : ''
			}

			updates.areaLandingStage = {
				...state.areaLandingStage,
				required: isRequiredForLanding,
				value: isRequiredForLanding ? state.areaLandingStage.value : ''
			}

			updates.boatPierDeclaration = {
				...state.boatPierDeclaration,
				required: isRequiredForPier,
				value: isRequiredForPier
					? state.typesOfStructureBoat.value
					: String(false)
			}

			updates.areaPier = {
				...state.areaPier,
				required: isRequiredForPier,
				value: isRequiredForPier ? state.areaPier.value : ''
			}

			break
		}

		case STEP4_4FIELDS.typesOfStructureBoat: {
			const isSectionA = isGR6_4SectionA(state.typesOfStructureBoat.value)
			const isSectionB = isGR6_4SectionB(state.typesOfStructureBoat.value)
			const isSectionC = isGR6_4SectionC(state.typesOfStructureBoat.value)

			const fieldsSectionC = [
				STEP4_4FIELDS.areaBoatShelter,
				STEP4_4FIELDS.widthBoatShelter,
				STEP4_4FIELDS.depthBoatShelter,
				STEP4_4FIELDS.heightBoatShelter,
				STEP4_4FIELDS.boatShelterMaterial
			]

			updates.typesOfStructureBoatOther = {
				...state.typesOfStructureBoatOther,
				required: isSectionA,
				value: isSectionA ? state.typesOfStructureBoatOther.value : ''
			}

			updates.areaPier = {
				...state.areaPier,
				required: isSectionB,
				value: isSectionB ? state.areaPier.value : ''
			}

			fieldsSectionC.forEach((value) => {
				if (state[value]) {
					updates[value] = {
						...state[value],
						required: isSectionC,
						value: isSectionC ? state[value].value : ''
					}
				}
			})
			break
		}
		case STEP4_4FIELDS.boatPierDeclaration: {
			const isRequired = state.boatPierDeclaration.value === false
			updates.boatPierDeclaration = {
				...state.boatPierDeclaration,
				required: isRequired,
				value: isRequired ? '' : state.boatPierDeclaration.value
			}
		}
		// ! STEP 4.5
		case STEP4_5FIELDS.typesOfWorkDiverse: {
			const isSectionA = isTypeOfWorkSectionA(state.typesOfWorkDiverse.value)
			const isSectionB = isTypeOfWorkSectionB(state.typesOfWorkDiverse.value)

			//* SECTION A
			// Q12
			updates.areaScreen = {
				...state.areaScreen,
				required: isSectionA,
				value: isSectionA ? state.areaScreen.value : ''
			}

			//* SECTION B
			// Q13
			updates.areaOutdoorStorage = {
				...state.areaOutdoorStorage,
				required: isSectionB,
				value: isSectionB ? state.areaOutdoorStorage.value : ''
			}

			break
		}

		// *********************************************************
		// ! STEP 5
		/**  @isWorkDueToDisaster  Effectuez-vous ces travaux à la suite d'une catastrophe? */
		case STEP5FIELDS.isWorkDueToDisaster: {
			const isYes = value == 'true'

			updates.workDueToDisasterPrecision = {
				...state.workDueToDisasterPrecision,
				required: isYes,
				value: isYes ? state.workDueToDisasterPrecision.value : ''
			}
			break
		}

		/**  @whoWillBeInChargeOfWork  Qui sera en charge des travaux? */
		/**  @entrepreneurUnknown1  Je ne connais pas encore l'entrepreneur */
		case STEP5FIELDS.whoWillBeInChargeOfWork:
		case STEP5FIELDS.entrepreneurUnknown1: {
			const fieldsToMakeUpdate = [
				STEP5FIELDS.companyNameExecutingWork,
				STEP5FIELDS.companyNEQNumber,
				STEP5FIELDS.companyRBQNumber,
				STEP5FIELDS.companyRepresentativeName,
				STEP5FIELDS.companyRepresentativePhoneNumber,
				STEP5FIELDS.companyRepresentativeEmail,
				STEP5FIELDS.companyAddress,
				STEP5FIELDS.companyCity,
				STEP5FIELDS.companyPostalCode
			]

			let isRequired = false
			if (changedField == STEP5FIELDS.whoWillBeInChargeOfWork) {
				// If the whoWillBeInChargeOfWork field is updated, we reset the value to false
				updates.entrepreneurUnknown1 = {
					...state.entrepreneurUnknown1,
					value: 'false'
				}

				isRequired = !(
					value == REQUEST_CONFIGS_KEYS.whoWillBeInChargeOfWork.firstOption
				)
			} else {
				isRequired = !(value.toString() == 'true')
			}

			fieldsToMakeUpdate.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isRequired,
						value: isRequired ? state[key].value : ''
					}
				}
			})
			break
		}

		// *********************************************************
		// ! STEP 5
		case STEP6FIELDS.isWaterAndSewerConnectionRequired: {
			// !important RESET FIELDS of Steps 6
			// const isGR7_1 = isGR7_1RenovationOrDevelopment(state.whatToDo.value)

			const fieldsToReset = Object.values(STEP6FIELDS).splice(1) // remove the first element of the array

			if (value == 'false') {
				fieldsToReset.forEach((element) => {
					if (state[element]) {
						switch (element) {
							case STEP6FIELDS.entrepreneurUnknown:
								updates.entrepreneurUnknown = {
									...state.entrepreneurUnknown,
									value: 'false'
								}
								break

							default:
								updates[element] = {
									...state[element],
									required: false,
									value: ''
								}
						}
					}
				})
			}
			break
		}

		case STEP6FIELDS.plannedWork: {
			const isAdditionalInfoQ44Required = value.includes(
				REQUEST_CONFIGS_KEYS.plannedWork.seventhOption
			)
			updates.additionalInfoQ44 = {
				...state.additionalInfoQ44,
				required: isAdditionalInfoQ44Required,
				value: isAdditionalInfoQ44Required ? state.additionalInfoQ44.value : ''
			}

			const isEntrepreneurFieldsRequired =
				pageUtils.isEntrepreneurFieldsRequired(value) &&
				state?.entrepreneurUnknown.value?.toString() == 'false'

			const entrepreneurFields = [
				STEP6FIELDS.entrepreneurName,
				STEP6FIELDS.entrepreneurRBQNumber,
				STEP6FIELDS.entrepreneurRepresentativeName,
				STEP6FIELDS.entrepreneurRepresentativePhoneNumber,
				STEP6FIELDS.entrepreneurRepresentativeEmail,
				STEP6FIELDS.entrepreneurRepresentativeAddress,
				STEP6FIELDS.entrepreneurRepresentativeCity,
				STEP6FIELDS.entrepreneurRepresentativePostalCode
			]

			entrepreneurFields.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isEntrepreneurFieldsRequired,
						value: isEntrepreneurFieldsRequired ? state[key].value : ''
					}
				}
			})
			break
		}

		case STEP6FIELDS.entrepreneurUnknown: {
			const isEntrepreneurFieldsRequired =
				pageUtils.isEntrepreneurFieldsRequired(state.plannedWork.value) &&
				value?.toString() == 'false'

			const entrepreneurFields = [
				STEP6FIELDS.entrepreneurName,
				STEP6FIELDS.entrepreneurRBQNumber,
				STEP6FIELDS.entrepreneurRepresentativeName,
				STEP6FIELDS.entrepreneurRepresentativePhoneNumber,
				STEP6FIELDS.entrepreneurRepresentativeEmail,
				STEP6FIELDS.entrepreneurRepresentativeAddress,
				STEP6FIELDS.entrepreneurRepresentativeCity,
				STEP6FIELDS.entrepreneurRepresentativePostalCode
			]

			entrepreneurFields.forEach((key) => {
				if (state[key]) {
					updates[key] = {
						...state[key],
						required: isEntrepreneurFieldsRequired,
						value: isEntrepreneurFieldsRequired ? state[key].value : ''
					}
				}
			})
			break
		}

		default:
			break
	}

	return updates
}

// --- 3. Reducer centralisé ---
export const inputReducer = (state, action) => {
	switch (action.type) {
		case 'UPDATE_INPUT': {
			const { name, value } = action
			let newState = {
				...state,
				[name]: {
					...state[name],
					value
				}
			}
			const linked = updateLinkedFields(newState, name, value)
			return {
				...newState,
				...linked
			}
		}
		case 'RESET':
			return action.payload
		default:
			return state
	}
}
